// The banner's side labels on one line (Seán, 2026-10-22; issues BN1, BN2): "VIZBI Online" and
// "Nov 3, 18 & 19, 2026" inline, both at one size, as large as fits beside the centre control
// (Register Now, the countdown…) and no larger than its text. In turn: the year, only at the full
// size (never smaller text to keep it); without it, if at least the stacked size; then tighter,
// "Nov 3, 18, 19", down to MIN_SIZE; then with tighter letter spacing, at whatever size fits:
// never stacked (Seán, 2026-10-23: "the goal would be never to have to stack").
// Measured from the centre's real width, so it follows the phase and the countdown.
import {nextTick, onMounted, ref, watch} from "vue";
import {useEventListener, useResizeObserver} from "@vueuse/core";

const GAP = {narrow: 8, medium: 16}; // px between a label and the centre; wider screens 24
const JITTER = 6; // px: the countdown's digits change its width a little every second
const MIN_SIZE = 11; // px: smaller, the last step (tighter letter spacing) instead
const TIGHT = "-0.03em"; // letter spacing, the last step

// a label's width at 1px, its lines inline (as BannerLabel draws them: 0.25em apart)
function getEmWidth(lines, fontFamily, letterSpacing = "normal") {
  const span = document.createElement("span");
  span.style.cssText = "position:absolute;visibility:hidden;white-space:nowrap;"
    + `font-weight:bold;font-size:100px;font-family:${fontFamily};letter-spacing:${letterSpacing}`;
  const getHtml = (line) => {
    if (line.isItalic) return `<i>${line.text}</i>`;
    if (!line.flag) return line;
    return `<span style="font-weight:normal">${line.flag}</span> ${line.suffix || ""}`;
  };
  span.innerHTML = lines.map(getHtml).join("&nbsp;");
  document.body.appendChild(span);
  const width = span.getBoundingClientRect().width / 100;
  span.remove();
  return width;
}

// the room each side has: from the banner's padding to the centre control, less the gap
function getRoom(banner, centre, displayWidth) {
  const padding = parseFloat(getComputedStyle(banner).paddingLeft);
  const left = centre.getBoundingClientRect().left - banner.getBoundingClientRect().left;
  return left - padding - (GAP[displayWidth] ?? 24);
}

// the size both labels take inline (the wider decides), capped at the centre's text size
function getInlineSize(room, cap, widths) {
  return Math.min(cap, room / Math.max(...widths));
}

// Register Now's side padding (Seán, 2026-10-23): with the viewer's flag, as little as a quarter
// (0.33em) where the dates need the room, else as much as is spare up to its full 1.33em. Both
// in px: what it has now, the trimmed and the full (null: no pill in the centre, or no flag)
function getPillPadding(centre, inline) {
  const pill = centre.querySelector(":scope > a");
  const hasFlag = inline.rightShort?.some((line) => line.flag);
  if (!pill || !hasFlag) return null;
  const style = getComputedStyle(pill);
  const em = parseFloat(style.fontSize);
  return {now: parseFloat(style.paddingLeft), tight: 0.33 * em, full: 1.33 * em};
}

// the pill's padding: the trimmed one, plus what the labels leave spare (each side), up to full
function getPillPaddingFor(pad, slack) {
  if (!pad) return "";
  return `${Math.round(pad.tight + Math.max(0, Math.min(pad.full - pad.tight, slack)))}px`;
}

const STACKED = {mode: "stacked", fontSize: "", letterSpacing: "", pillPadding: ""};

export function getFit({banner, centre, inline, displayWidth}) {
  if (!inline || !banner || !centre) return STACKED;
  const {fontSize, fontFamily} = getComputedStyle(banner);
  const cap = parseFloat(fontSize);
  const stacked = cap * (displayWidth === "medium" ? 0.65 : 0.8); // as the stacked labels
  // the room as if the pill had its trimmed padding: the same whatever it has now (no see-saw)
  const pad = getPillPadding(centre, inline);
  const room = getRoom(banner, centre, displayWidth) + (pad ? pad.now - pad.tight : 0);
  const getSize = (right, spacing) => {
    const widths = [getEmWidth(inline.left, fontFamily, spacing),
      getEmWidth(right, fontFamily, spacing)];
    const size = getInlineSize(room, cap, widths);
    return {size, slack: room - size * Math.max(...widths)};
  };
  const steps = [["inline", inline.right, "", cap], ["inlineShort", inline.rightShort, "", stacked],
    ["inlineCompact", inline.rightCompact, "", MIN_SIZE],
    ["inlineCompact", inline.rightCompact, TIGHT, 0]];
  for (const [mode, right, letterSpacing, least] of steps) {
    const {size, slack} = getSize(right, letterSpacing || "normal");
    if (size < least) continue;
    const pillPadding = getPillPaddingFor(pad, slack);
    return {mode, fontSize: `${Math.floor(size * 10) / 10}px`, letterSpacing, pillPadding};
  }
  return STACKED;
}

// drawn, a label may be wider than measured (a flag emoji: Apple's is wider than the measure's;
// it touched Register Now on Seán's iPhone, 2026-10-23): the size shrunk by the overrun, so both
// labels keep their gap to the centre
function getCorrectedFit(fit, {banner, centre, displayWidth}) {
  if (fit.mode === "stacked" || !banner || !centre) return fit;
  const room = getRoom(banner, centre, displayWidth);
  const selector = ".left-text .banner-label, .right-text .banner-label";
  const labels = [...banner.querySelectorAll(selector)];
  const widest = Math.max(...labels.map((label) => label.getBoundingClientRect().width));
  if (widest <= room) return fit;
  const size = Math.floor(parseFloat(fit.fontSize) * (room / widest) * 10) / 10;
  return {...fit, fontSize: `${size}px`};
}

// labels: a getter for the banner's labels (with .inline); displayWidth: a ref
export default function useBannerFit({banner, centre, labels, displayWidth}) {
  const fit = ref(STACKED);
  let centreWidth = 0;
  const refit = async () => {
    const parts = {banner: banner.value, centre: centre.value, displayWidth: displayWidth.value};
    fit.value = getFit({...parts, inline: labels().inline});
    await nextTick();
    fit.value = getCorrectedFit(fit.value, parts);
  };
  useResizeObserver(banner, refit);
  useResizeObserver(centre, ([entry]) => {
    if (Math.abs(entry.contentRect.width - centreWidth) < JITTER) return;
    centreWidth = entry.contentRect.width;
    refit();
  });
  watch([labels, displayWidth], refit, {flush: "post"});
  onMounted(async () => {
    await document.fonts?.ready;
    refit();
  });
  if (document.fonts) useEventListener(document.fonts, "loadingdone", refit);
  return fit;
}
